import { Controller, type Control, type FieldErrors } from 'react-hook-form'

import type { SelectOption } from '../../common/designs'
import { InputField, Select, Text } from '../../common/designs'
import { PROCESS_FORM_TIP_BOX } from './constants'
import DimensionListOrCustomToggle from './DimensionListOrCustomToggle'
import type { ProcessFormFields } from './types'

interface Props {
  control: Control<ProcessFormFields>
  errors: FieldErrors<ProcessFormFields>
  dimensionOptions: SelectOption[]
  isSelectUsedWithIdName: boolean
  setIsSelectUsedWithIdName: (isUsingSelect: boolean) => void
  isSelectUsedWithStateTag: boolean
  setIsSelectUsedWithStateTag: (isUsingSelect: boolean) => void
}

const ProcessFormBasicTabFields = ({
  control,
  errors,
  dimensionOptions,
  isSelectUsedWithIdName,
  setIsSelectUsedWithIdName,
  isSelectUsedWithStateTag,
  setIsSelectUsedWithStateTag,
}: Props) => (
  <>
    <div className={PROCESS_FORM_TIP_BOX}>
      <Text size='s' color='gray' className='leading-relaxed'>
        NOTE: Save stores the full process. Step-matching rules use the values from the last Apply
        on each rule card. New rows from Add rule are only included after you apply that card once;
        Save is always available.
      </Text>
    </div>

    <div className='flex flex-col gap-3.5'>
      <Controller
        name='name'
        control={control}
        render={({ field }) => (
          <InputField label='Process name' error={errors.name?.message} {...field} autoFocus />
        )}
      />

      <Controller
        name='process_identifier_name'
        control={control}
        render={({ field }) => (
          <div className='space-y-1.5'>
            <div className='flex flex-wrap items-center justify-between gap-x-3 gap-y-1'>
              <span className='text-sm font-medium text-gray-700'>Process identifier name</span>
              <DimensionListOrCustomToggle
                isUsingDimensionList={isSelectUsedWithIdName}
                onIsUsingDimensionListChange={setIsSelectUsedWithIdName}
              />
            </div>
            {isSelectUsedWithIdName ? (
              <Select
                error={errors.process_identifier_name?.message}
                options={dimensionOptions}
                {...field}
              />
            ) : (
              <InputField error={errors.process_identifier_name?.message} {...field} />
            )}
          </div>
        )}
      />

      <Controller
        name='process_category'
        control={control}
        render={({ field }) => (
          <InputField
            label='Process category name'
            error={errors.process_category?.message}
            {...field}
          />
        )}
      />

      <Controller
        name='state_tag_key'
        control={control}
        render={({ field }) => {
          const { value, ...rest } = field
          return (
            <div className='space-y-1.5'>
              <div className='flex flex-wrap items-center justify-between gap-x-3 gap-y-1'>
                <span className='text-sm font-medium text-gray-700'>State tag key</span>
                <DimensionListOrCustomToggle
                  isUsingDimensionList={isSelectUsedWithStateTag}
                  onIsUsingDimensionListChange={setIsSelectUsedWithStateTag}
                />
              </div>
              {isSelectUsedWithStateTag ? (
                <Select error={errors.state_tag_key?.message} options={dimensionOptions} {...field} />
              ) : (
                <InputField
                  error={errors.state_tag_key?.message}
                  value={value === null ? undefined : value}
                  {...rest}
                />
              )}
            </div>
          )
        }}
      />

      <Controller
        name='initial_visible_variant_amount'
        control={control}
        render={({ field }) => (
          <InputField
            label='Initial visible variant amount'
            type='number'
            min={1}
            error={errors.initial_visible_variant_amount?.message}
            description={`Default amount of variants selected in the process' "Variants" view`}
            {...field}
          />
        )}
      />
    </div>
  </>
)

export default ProcessFormBasicTabFields
